import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Box, Divider, Grid, Switch, Typography, useTheme, TextField, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, CircularProgress, Collapse, Select, MenuItem, FormControl } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { BannerStatic } from '../../../components'
import { TenantEditSlice, MSATenantConfigSlice } from '../../../reducers'
import { removeLeadingZeros } from '../../../util/form-utils'
import { useTenantFormContext } from '../tenant-form-context'
import { Controller } from 'react-hook-form'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import { useStyles } from './style'

const SpinnerIcon = () => (
  <CircularProgress size={20} sx={{ mx: 2 }} />
)
const MSASettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const { isEdit, form, tenant } = useTenantFormContext()
  const { setValue, watch } = form
  const features = watch('features')
  const MSA = features?.assessment?.enabled ?? features.msa
  const accelerate = features?.accelerate?.enabled ?? false
  const tenantRegion = tenant?.region || tenant?.cognitoSettings?.userPoolId?.split('_')[0]

  const { assessmentExternalId: assessmentId, numberOfUsers, metaSkills, benchmarkScores, usersList, isTrial, trialSupported } = useSelector(MSATenantConfigSlice.selectors.selectTenantMSAConfig)
  const [msaConfigOptions, setMsaConfigOptions] = useState({
    assessmentId,
    numberOfUsers,
    benchmarkScores,
    metaSkills,
    isTrial
  })

  const [expanded, setExpanded] = useState()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!isEdit) {
      setValue('features.assessment.enabled', false)
      return
    }
    const hasSkillBites = tenant.features?.assessment?.enabled ?? tenant?.features?.msa
    setValue('features.assessment.enabled', hasSkillBites)
  }, [setValue, isEdit, tenant])

  const [assessmentList, setAssessmentList] = React.useState([])

  const [loadingAssessment, setLoadingAssessment] = useState(false)
  const [showTrial, setShowTrial] = useState(false)
  const [tenantUpdate, msaGetAssessmentPreview, getAssessments] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'MSA configuration updated' })
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: (error) => {
        if (error.code === 'ERROR_FORBIDDEN') {
          return BannerStatic.show({
            label: error.description,
            isError: true
          })
        }
        return BannerStatic.show({
          label: 'There was an error saving the toolkit features configuration',
          isError: true
        })
      }
    },
    {
      eventName: 'msaGetAssessmentPreview',
      onSuccess: (payload) => {
        setShowTrial(payload.trialSupported)
        setMsaConfigOptions({
          assessmentId: payload.sk,
          numberOfUsers: payload.numberOfUsers ?? 0,
          benchmarkScores: payload.benchmarkScores,
          metaSkills: payload.metaSkills,
          isTrial: payload.isTrial
        })
        setLoadingAssessment(false)
      },
      onError: () => {
        setLoadingAssessment(false)
        BannerStatic.show({
          label: 'Unable to fetch assessment details',
          isError: true
        })
      }
    },
    {
      eventName: 'msaGetAssessments',
      onSuccess: (payload) => {
        if (payload) {
          setAssessmentList(payload)
          setLoadingAssessment(false)
        }
      },
      onError: () => {
        setLoadingAssessment(false)
        BannerStatic.show({
          label: 'There was an error when getting assessment list.',
          isError: true
        })
      }
    }
  ])

  useEventsOnViewLoad(() => {
    if (!assessmentList.length) {
      getAssessments.trigger()
      setLoadingAssessment(true)
    }
  }, [])

  useEventsOnViewLoad(() => {
    setMsaConfigOptions({ assessmentId, numberOfUsers, benchmarkScores, metaSkills, isTrial })
    setExpanded(metaSkills[0] ? metaSkills[0].id : false)
    setShowTrial(trialSupported)
  }, [])

  const handleMSASwitch = () => {
    setValue('features.assessment.enabled', !MSA)
  }

  const handleMSASettings = () => setIsCollapsed(isCollapsed => !isCollapsed)

  const scoreDictionary = msaConfigOptions.benchmarkScores.reduce((acc, { metaSkillId, skills }) => {
    skills.forEach(({ skillId, score }) => {
      acc[`${metaSkillId}_${skillId}`] = score
    })
    return acc
  }, {})

  const getScore = (metaSkillId, skillId) => scoreDictionary[`${metaSkillId}_${skillId}`]

  const updateScore = ({ benchmarkScores, metaSkillId, skillId, newScore }) => {
    return benchmarkScores.map(metaSkill => {
      if (metaSkill.metaSkillId === metaSkillId) {
        return {
          ...metaSkill,
          skills: metaSkill.skills.map(skill =>
            skill.skillId === skillId
              ? { ...skill, score: newScore }
              : skill
          )
        }
      }
      return metaSkill
    })
  }

  const handleTrialSwitch = (event) => {
    setMsaConfigOptions({ ...msaConfigOptions, isTrial: event.target.checked })
  }

  const handleAssessmentChangeType = (event) => {
    const assessmentExternalId = event.target.value
    setMsaConfigOptions({ ...msaConfigOptions, assessmentId: assessmentExternalId })
    setLoadingAssessment(true)
    msaGetAssessmentPreview.trigger({ tenantId: tenant.pk, assessmentExternalId, region: tenantRegion })
  }

  const handleChangeNumberOfUsers = (event) => {
    event.target.value = removeLeadingZeros(event.target.value)
    const newValue = Number(event.target.value)
    setMsaConfigOptions({ ...msaConfigOptions, numberOfUsers: newValue })
  }

  const handleBenchmarkScoringChange = (metaSkillId, skillId, event) => {
    event.target.value = removeLeadingZeros(event.target.value)
    const newScore = Number(event.target.value)

    const updatedBenchmarkScores = updateScore({
      benchmarkScores: msaConfigOptions.benchmarkScores,
      metaSkillId,
      skillId,
      newScore
    })

    setMsaConfigOptions((prevData) => ({
      ...prevData,
      benchmarkScores: updatedBenchmarkScores
    }))
  }

  const handleAccordionExpanded = (metaSkillId) => (_event, isExpanded) => {
    setExpanded(isExpanded ? metaSkillId : false)
  }

  const handleSave = () => {
    if (accelerate && !MSA) {
      return BannerStatic.show({
        label: 'You need to disable Accelerate to turn off Manager Skills Assessment',
        isWarning: true
      })
    }
    tenantUpdate.registerAdhocOnSuccessListener(() => {
      dispatch(MSATenantConfigSlice.actions.updateMSATenantConfig(msaConfigOptions))
    })
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        msaConfigOptions: {
          enabled: MSA,
          ...(MSA && msaConfigOptions)
        }
      },
      region: tenant?.region || tenant.cognitoSettings.userPoolId.split('_')[0]
    })
  }

  const isLoading = tenantUpdate.isWorking
  const isValid = msaConfigOptions.numberOfUsers >= usersList.length

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box border={1} borderRadius={1} borderColor='#E0E0E0' sx={{ p: spacing(1, 4) }}>
          <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
            <Typography variant='body2' sx={{ mt: spacing(3.5) }}>
              Manager Skills Assessments
            </Typography>
            <Box display='flex' justifyContent='flex-end' alignItems='center' padding={spacing(1)}>
              {MSA && !isLoading && isEdit && (
                <>
                  <Box
                    visibility={MSA ? 'visible' : 'hidden'}
                    sx={{
                      ':hover': {
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <List component='nav' aria-label='Device settings'>
                      <ListItem onClick={handleMSASettings} aria-busy={loadingAssessment} aria-haspopup='true' aria-controls='lock-menu' aria-label='MSA Settings'>
                        <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant='body2'>MSA Settings</Typography>
                          {isCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                  <Divider variant='middle' flexItem orientation='vertical' sx={{ mx: spacing(2), borderColor: 'rgba(0, 0, 0, 0.38)' }} />
                </>
              )}
              <Controller
                name='features.assessment.enabled'
                control={form.control}
                render={({ field }) => (
                  <Switch
                    color='primary'
                    checked={!!field.value}
                    onChange={handleMSASwitch}
                    disabled={isLoading}
                    data-test='msa-product-toggle'
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>
          {isLoading && (
            <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
              <CircularProgress />
              <Typography variant='body2' sx={{ mt: spacing(2) }}>
                {tenantUpdate.isWorking ? 'Updating MSA Configuration...' : 'Loading MSA Configuration...'}
              </Typography>
            </Box>
          )}
          <Collapse in={isCollapsed}>
            {MSA && !isLoading && isEdit && (
              <>
                <Box display='flex' flexDirection='column' gap={2} mb={spacing(1)}>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box width='50%'>
                      <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                        Assessment Type
                      </Typography>
                      <Typography variant='body2' sx={{ mt: spacing(1) }}>
                        Select your preferred assessment type
                      </Typography>
                    </Box>

                    <Box display='flex' alignItems='center'>
                      <FormControl sx={{ m: 1, width: 290 }}>
                        <Select
                          disabled={loadingAssessment}
                          labelId='msa-setting-assessment-type-label'
                          id='msa-setting-assessment-type'
                          value={msaConfigOptions.assessmentId}
                          onChange={handleAssessmentChangeType}
                          IconComponent={loadingAssessment ? SpinnerIcon : undefined}
                        >
                          {loadingAssessment && !assessmentList.length
                            ? (<MenuItem value={assessmentId}>Loading</MenuItem>)
                            : assessmentList.map((assessment) => (<MenuItem key={assessment.externalId} value={assessment.externalId}>{assessment.title}</MenuItem>))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box sx={{ opacity: loadingAssessment ? 0.5 : 1, pointerEvents: loadingAssessment ? 'none' : 'auto' }}>
                    {showTrial && (
                      <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Box width='50%'>
                          <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                            Trial Assessment
                          </Typography>
                          <Typography variant='body2' sx={{ mt: spacing(1) }}>
                            Activate this Switch to enable a trial version of the Manager Skills Assessment.
                            This will allow the client to have the complete MindTools Manager Skills Assessment experience,
                            but on a reduced version of the assessment.
                          </Typography>
                        </Box>
                        <Switch
                          color='primary'
                          checked={msaConfigOptions.isTrial}
                          onChange={handleTrialSwitch}
                          disabled={!MSA}
                          data-test='msa-product-trial-toggle'
                        />
                      </Box>
                    )}
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Box width='50%'>
                        <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                          Number of Users
                        </Typography>
                        <Typography variant='body2' sx={{ mt: spacing(1) }}>
                          This is the maximum number of users that the client is paying to have on their assessment.
                          This information will feed the reporting tool and clients will not be cut off if their users exceed the number you specify.
                        </Typography>
                      </Box>
                      <TextField
                        label='Number of Users'
                        type='number'
                        value={msaConfigOptions.numberOfUsers}
                        onChange={handleChangeNumberOfUsers}
                        disabled={!MSA}
                        InputProps={{ inputProps: { min: usersList.length } }}
                        error={msaConfigOptions.numberOfUsers < usersList.length}
                        helperText={`Minimum Number of Users: ${usersList.length}`}
                        data-test='msa-users-number'
                      />
                    </Box>
                    <Box sx={{ marginY: 4 }} />
                    <Box mt={spacing(2)}>
                      <Typography variant='body1' sx={{ fontWeight: 500 }}>
                        Skill Benchmark Scoring
                      </Typography>
                      <Typography variant='body2' sx={{ mt: spacing(1) }}>
                        This section is reserved for the benchmark score for each skill in the assessment. Each Meta Skill has a set of skills that are scored.
                      </Typography>
                      <Box sx={{ marginY: 4 }} />
                      {msaConfigOptions?.metaSkills?.map((metaSkillObj) => (
                        <Accordion
                          key={metaSkillObj.id}
                          expanded={expanded === metaSkillObj.id}
                          onChange={handleAccordionExpanded(metaSkillObj.id)}
                          data-test={`msa-metaSkill-benchmark-accordion-${metaSkillObj.order - 1}`}
                          classes={{ root: classes.msaAccordion, expanded: classes.msaAccordionExpanded }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant='subtitle1'>{metaSkillObj.name}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <List>
                              {metaSkillObj.skills.map((skillObj) => (
                                <ListItem key={skillObj.id}>
                                  <ListItemText disableTypography primary={<Typography variant='body2' className={classes.msaSubtitleMap}>{skillObj.name}</Typography>} />
                                  <Typography variant='body2' className={classes.msaScoreLabel}>Score:</Typography>
                                  <TextField
                                    className={classes.msaScoreInput}
                                    type='number'
                                    value={getScore(metaSkillObj.id, skillObj.id)}
                                    onChange={(e) => handleBenchmarkScoringChange(metaSkillObj.id, skillObj.id, e)}
                                    inputProps={{ min: 0, max: 100 }}
                                    data-test={`msa-metaSkill-benchmark-${metaSkillObj.order - 1}-score-${skillObj.order - 1}`}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Collapse>
          {isEdit && (
            <Box>
              <Divider sx={{ mb: spacing(1), pt: spacing(2) }} />
              <Box className={classes.ctnControls} display='flex' justifyContent='flex-end'>
                <ActionButton
                  onClick={handleSave}
                  disabled={isLoading || !isValid}
                  variant='primary'
                  dataTest='msa-save-button'
                >
                  Save
                </ActionButton>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default MSASettings
